import React from 'react';
import { useTranslation } from 'localization';
import CustomTabs from '../../components/atoms/CustomTabs/CustomTabs';
import CustomDialogAtom from '../../components/atoms/CustomDialog/CustomDialogAtom';
import type { Adapter } from '../types';
import './layoutAdapters.scss';

const ALIGNMENT_MAP: Record<string, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  'space-between': 'space-between',
};

function childIds(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
}

export const ColumnAdapter: Adapter = ({ componentId, props, ctx }) => (
  <div
    data-component-id={componentId}
    className="layout-adapter__column"
    style={{ gap: props.gap as string | undefined, padding: props.padding as string | undefined }}
  >
    {ctx.renderChildren(childIds(props.children))}
  </div>
);

export const RowAdapter: Adapter = ({ componentId, props, ctx }) => (
  <div
    data-component-id={componentId}
    className="layout-adapter__row"
    style={{
      gap: props.gap as string | undefined,
      justifyContent: props.alignment ? ALIGNMENT_MAP[props.alignment as string] ?? 'flex-start' : undefined,
    }}
  >
    {ctx.renderChildren(childIds(props.children))}
  </div>
);

export const CardAdapter: Adapter = ({ componentId, props, ctx }) => {
  const title = props.title !== undefined ? (ctx.resolveValue(props.title) as string) : undefined;
  return (
    <div data-component-id={componentId} className="layout-adapter__card">
      {title && <div className="layout-adapter__card-title">{title}</div>}
      <div className="layout-adapter__card-body">{ctx.renderChildren(childIds(props.children))}</div>
    </div>
  );
};

export const TabsAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const tabs = Array.isArray(props.tabs) ? (props.tabs as Array<{ label: unknown; children: unknown }>) : [];
  return (
    <div data-component-id={componentId}>
      <CustomTabs
        tabs={tabs.map((tab, index) => ({
          id: `${componentId}::tab-${index}`,
          header:
            (ctx.resolveValue(tab.label) as string) ?? t('space.render.tabFallback', { index: index + 1 }),
          content: ctx.renderChildren(childIds(tab.children)),
        }))}
      />
    </div>
  );
};

export const ModalAdapter: Adapter = ({ componentId, props, ctx }) => {
  const title = (ctx.resolveValue(props.title) as string) ?? '';
  const visible = Boolean(ctx.resolveValue(props.visible));
  const onCloseAction = props.onClose as string | undefined;

  const handleHide = () => {
    if (onCloseAction) ctx.onAction({ name: onCloseAction, componentId });
  };

  return (
    <div data-component-id={componentId}>
      <CustomDialogAtom header={title} visible={visible} onHide={handleHide}>
        {ctx.renderChildren(childIds(props.children))}
      </CustomDialogAtom>
    </div>
  );
};

export const ListAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const items = ctx.resolveValue(props.items) as unknown[] | undefined;
  const emptyMessage = props.emptyMessage !== undefined ? (ctx.resolveValue(props.emptyMessage) as string) : undefined;

  if (!items || items.length === 0) {
    return (
      <div data-component-id={componentId} className="layout-adapter__list-empty">
        {emptyMessage ?? t('space.render.noItems')}
      </div>
    );
  }

  // `template` names a sibling component entry that is rendered once per item. Each instance
  // resolves its `$bind` pointers against a data model overlaid with that item, so
  // `{ $bind: '/item/name' }` inside the template addresses the row it belongs to.
  const template = props.template as { id?: string; component?: string; [key: string]: unknown } | undefined;
  if (!template?.id) return null;

  return (
    <div data-component-id={componentId} className="layout-adapter__list">
      {items.map((item, index) => (
        <div key={`${componentId}::item-${index}`} className="layout-adapter__list-item">
          {ctx.renderChildInScope(template.id as string, { item, index }, `::item-${index}`)}
        </div>
      ))}
    </div>
  );
};
